import React, { useEffect, useState } from "react";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import DiscordSavedMessages from "../../../types/discord_saved_messages";
import moment from "moment";
import { useQuery, useQueryClient, UseQueryResult } from "react-query";

/**
 * fetch all stored discord messages for a workspace
 * @param  deleteMessagesDB - delete messages
 * @param  error
 */
export type DiscordSavedMessageTableData = DiscordSavedMessages & {
  tags_msgs: { name: string; color: string; id: number }[];
  raw_channel_name: string;
  saved_on: string;
  raw_tags: string;
};

export default function useDiscordMessages(): {
  error: boolean;
  deleteMessagesDB: (messages: React.Key[]) => Promise<void>;
  loadDiscordSavedMessages: () => UseQueryResult<
    DiscordSavedMessageTableData[],
    Error
  >;
} {
  const [error, setError] = useState<boolean>(false);
  const supabase = getSupaClient();
  const guildId = localStorage.getItem("guild-id") || "";
  const queryClient = useQueryClient();

  /**
   * Load all saved messages for a given guild
   */
  const loadDiscordSavedMessages = () => {
    const fetchAllSavedMessages = async () => {
      const { data, error } = await supabase
        .from<
          DiscordSavedMessages & {
            tags_msgs: { name: string; color: string; id: number }[];
          }
        >("discord_saved_messages")
        .select("*, tags_msgs(id, name, color)")
        .match({ guild: guildId })
        .order("created_at", { ascending: false });

      if (data && data.length >= 0 && Array.isArray(data)) {
        return data;
      } else if (error) {
        throw new Error(error.message);
      } else {
        throw new Error(`Failed to fetch saved messages: ${guildId}`);
      }
    };

    return useQuery<
      (DiscordSavedMessages & {
        tags_msgs: { name: string; color: string; id: number }[];
      })[],
      Error,
      DiscordSavedMessageTableData[]
    >(["guildfetchAllSavedMessages", guildId], fetchAllSavedMessages, {
      select: (data) => {
        return data.map((data) => {
          return {
            ...data,
            channel_name: `#${data.channel_name}`,
            created_at_discord: moment(data.created_at_discord).format(
              "MM/DD/YYYY HH:mma"
            ),
            saved_on: moment(data.created_at).format("MM/DD/YYYY HH:mma"),
            raw_channel_name: data.channel_name,
            raw_tags: data.tags_msgs.map((t) => t.name).join(","),
          };
        });
      },
    });
  };

  /**
   * Remove message from the database
   */
  const deleteMessagesDB = async (messages: React.Key[]) => {
    try {
      const { error } = await supabase
        .from("discord_saved_messages")
        .delete()
        .in("discord_id", messages);

      if (error) {
        setError(true);
        console.error("deleteMessagesDB:", error);
      } else {
        window.analytics.track("Saved messages - message deleted");
      }
      queryClient.invalidateQueries("guildfetchAllSavedMessages");
    } catch (err) {
      setError(true);
      console.error("deleteMessagesDB:", err);
    }
  };

  return {
    deleteMessagesDB,
    error,
    loadDiscordSavedMessages,
  };
}
